import { useEffect, useRef, type CSSProperties } from 'react';
import {
  artFor,
  bodyArt,
  colourHex,
  LAYER_ORDER,
  TINTED,
  type LayerId,
  type Look,
} from '@/content/agentLook';

const images = new Map<string, Promise<HTMLImageElement>>();

function loadImage(url: string): Promise<HTMLImageElement> {
  let p = images.get(url);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error(`could not load ${url}`));
      };
      img.src = url;
    });
    images.set(url, p);
  }
  return p;
}

/**
 * The layer's art multiplied by a colour (keeping the art's own transparency), and with what a
 * hat covers cut out (hair under a hat).
 */
function prepared(
  img: HTMLImageElement,
  w: number,
  h: number,
  hex?: string,
  cut?: HTMLImageElement,
): HTMLCanvasElement | HTMLImageElement {
  if (!hex && !cut) return img;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) return c;
  g.drawImage(img, 0, 0, w, h);
  if (hex) {
    g.globalCompositeOperation = 'multiply';
    g.fillStyle = hex;
    g.fillRect(0, 0, w, h);
    g.globalCompositeOperation = 'destination-in';
    g.drawImage(img, 0, 0, w, h);
  }
  if (cut) {
    g.globalCompositeOperation = 'destination-out';
    g.drawImage(cut, 0, 0, w, h);
  }
  return c;
}

/** What to draw: each layer's art and tint, back to front; hair loses what a hat covers. */
function layersOf(look: Look): { layer: LayerId; url: string; tint?: string; cutBy?: string }[] {
  const hat = look.items.headgear;
  const cover = hat && hat !== 'none' ? artFor('headgear', `${hat}-cover`) : undefined;
  const back = look.items.back;
  // A back item's straps and front parts (its "-front" art) go over the suit, after the shoes.
  const backFront = back && back !== 'none' ? artFor('back', `${back}-front`) : undefined;
  const backTint = colourHex('back', look.colours.back);
  const drawnLayers: { layer: LayerId; url: string; tint?: string; cutBy?: string }[] =
    LAYER_ORDER.flatMap((layer) => {
      const id = look.items[layer];
      if (!id || id === 'none' || layer === 'face') return [];
      const art = layer === 'body' ? bodyArt(look.items.face, id) : artFor(layer, id);
      if (!art) return [];
      const tint = TINTED.has(layer) ? colourHex(layer, look.colours[layer]) : undefined;
      const drawn = {
        layer,
        url: art.url,
        ...(tint ? { tint } : {}),
        ...(layer === 'hair' && cover ? { cutBy: cover.url } : {}),
      };
      return [drawn];
    });
  if (backFront) {
    // After the body, suit and shoes (whichever comes last), before hair and headwear.
    const at =
      Math.max(
        ...['body', 'suit', 'shoes'].map((l) => drawnLayers.findLastIndex((d) => d.layer === l)),
      ) + 1;
    drawnLayers.splice(at, 0, {
      layer: 'back',
      url: backFront.url,
      ...(backTint ? { tint: backTint } : {}),
    });
  }
  return drawnLayers;
}

/**
 * The child's agent (docs/design/agent-builder.md): the look's layers stacked on one canvas, the
 * tinted ones recoloured. Redraws when the look changes; the figure keeps its place while new
 * layers load (the previous drawing stays until the new one is ready).
 */
export function AgentFigure({
  look,
  height,
  className,
  label,
  style,
}: {
  look: Look;
  /** CSS pixels. */
  height: number;
  className?: string | undefined;
  label?: string;
  /** Extra inline style (a close-up's offset). */
  style?: CSSProperties | undefined;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const layers = layersOf(look);
  const key = JSON.stringify(layers);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let cancelled = false;
    const parts = JSON.parse(key) as ReturnType<typeof layersOf>;
    void Promise.all(
      parts.map(async (p) => ({
        ...p,
        img: await loadImage(p.url),
        cut: p.cutBy ? await loadImage(p.cutBy) : undefined,
      })),
    )
      .then((loaded) => {
        if (cancelled || !loaded[0]) return;
        const first = loaded[0].img;
        const scale = Math.min(1, (height * window.devicePixelRatio) / first.naturalHeight);
        const w = Math.round(first.naturalWidth * scale);
        const h = Math.round(first.naturalHeight * scale);
        const g = canvas.getContext('2d');
        if (!g) return;
        canvas.width = w;
        canvas.height = h;
        g.clearRect(0, 0, w, h);
        for (const p of loaded) g.drawImage(prepared(p.img, w, h, p.tint, p.cut), 0, 0, w, h);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [key, height]);

  return (
    <canvas
      ref={ref}
      className={className}
      role="img"
      aria-label={label ?? 'Your agent'}
      style={{ ...style, height, width: 'auto', aspectRatio: '1024 / 1576' }}
    />
  );
}
